/**
 * CrewPushOptIn ✦
 *
 * Smart, deferred push opt-in banner. Renders exactly nothing
 * until the parent component flips `trigger` to true (which our
 * call sites do after the first swipe / save / apply / message —
 * NEVER on page load).
 *
 * Copy is opportunity-driven, not "allow notifications". Once
 * dismissed or granted, the choice is remembered in localStorage
 * so we don't pester.
 *
 * Service worker registration + subscription POST happen inline.
 * Falls back gracefully on unsupported browsers (Safari < 16.4).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lightning, BellRinging, X as XIcon, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORE_KEY = 'crew_push_opt_in_v1';

const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const urlBase64ToUint8 = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const browserSupportsPush = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && typeof Notification !== 'undefined';

const CrewPushOptIn = ({ trigger, source = 'crew' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!trigger) return;
    if (!browserSupportsPush()) return;
    const stored = (() => {
      try { return localStorage.getItem(STORE_KEY); }
      catch { return null; }
    })();
    // Already decided in either direction → stay quiet.
    if (stored === 'granted' || stored === 'dismissed') return;
    if (Notification.permission === 'granted') {
      try { localStorage.setItem(STORE_KEY, 'granted'); } catch {}
      return;
    }
    if (Notification.permission === 'denied') {
      try { localStorage.setItem(STORE_KEY, 'dismissed'); } catch {}
      return;
    }
    // Show after a short delay so the moment that triggered it
    // (swipe/save/apply/message) lands its own emotional beat first.
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [trigger]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORE_KEY, 'dismissed'); } catch {}
  };

  const enable = async () => {
    setBusy(true); setError('');
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      // Get VAPID key
      const keyRes = await axios.get(`${API}/push/public-key`);
      const vapid = keyRes.data?.vapid_public_key;
      if (!vapid) throw new Error('No VAPID key configured');
      // Request permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        dismiss();
        return;
      }
      // Subscribe
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8(vapid),
      });
      const subJson = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        source,
      });
      try { localStorage.setItem(STORE_KEY, 'granted'); } catch {}
      setSuccess(true);
      setTimeout(() => setOpen(false), 1600);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not enable notifications');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="crew-push-optin"
      role="dialog"
      aria-label="Enable event impulse notifications"
      style={{
        position: 'fixed', left: 16, right: 16, bottom: 20, zIndex: 90,
        maxWidth: 480, margin: '0 auto',
        padding: 16, borderRadius: 18,
        background: 'rgba(15,15,32,0.96)',
        border: `1px solid ${PINK}55`,
        boxShadow: `0 18px 40px rgba(0,0,0,0.55), 0 0 30px ${PURPLE}33`,
        backdropFilter: 'blur(14px)',
        color: TEXT, fontFamily: FONT_STACK,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 12,
        background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: `0 6px 16px ${PURPLE}55`,
      }}>
        {success ? <CheckCircle size={18} weight="fill" color="#fff" /> : <Lightning size={18} weight="fill" color="#fff" />}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 900, fontSize: 14.5, letterSpacing: '-0.01em' }}>
          {success ? "You're in ✦" : 'Want instant event opportunities near you? ✦'}
        </p>
        <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.45 }}>
          {success
            ? "We'll only ping for real moments — accepted shifts, urgent drops, fresh badges."
            : 'Urgent shifts, accepted applications, and badge moments — no spam.'}
        </p>
        {error && (
          <p data-testid="crew-push-optin-error" style={{ margin: '6px 0 0', color: PINK, fontSize: 12 }}>{error}</p>
        )}

        {!success && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={enable}
              disabled={busy}
              data-testid="crew-push-optin-enable"
              style={{
                padding: '8px 14px', borderRadius: 999, border: 'none',
                cursor: busy ? 'wait' : 'pointer', color: TEXT,
                fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                boxShadow: `0 8px 20px ${PURPLE}55`,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                opacity: busy ? 0.7 : 1,
              }}
            >
              <BellRinging size={12} weight="fill" />
              {busy ? 'Connecting…' : 'Turn on ✦'}
            </button>
            <button
              onClick={dismiss}
              data-testid="crew-push-optin-dismiss"
              style={{
                padding: '8px 12px', borderRadius: 999,
                background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                color: TEXT_MUTED, cursor: 'pointer',
                fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
              }}
            >
              Not now
            </button>
          </div>
        )}
      </div>

      <button
        onClick={dismiss}
        aria-label="Close"
        data-testid="crew-push-optin-close"
        style={{
          background: 'transparent', border: 'none', color: TEXT_MUTED,
          cursor: 'pointer', width: 22, height: 22, padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <XIcon size={13} weight="bold" />
      </button>
    </div>
  );
};

export default CrewPushOptIn;
